import { type QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { formatUnits, type Hex } from 'viem';
import { DEFAULT_TAX_RATE_BPS } from '@/lib/tax';
import { TAKEOVER_MANAGER_ABI, UGM_ABI, isV2Manager } from '@/lib/contracts';
import { TAKEOVER_TOKEN_ADDRESS } from '@/lib/constants/addresses';
import { formatUSD, parseGridId } from '@/lib/utils';
import type { SwapMode } from '@/components/modals/swap-modal';

export const DURATION_OPTIONS = [
  { label: '1 hour', weeks: 1 / (7 * 24) },
  { label: '1 day', weeks: 1 / 7 },
  { label: '7 days', weeks: 1 },
  { label: '1 month', weeks: 4 },
] as const;

export function calcDeposit(priceNum: number, runwayWeeks: number, decimals = 6, taxRateBps?: bigint): bigint {
  if (priceNum <= 0) return 0n;
  const rate = Number(taxRateBps ?? DEFAULT_TAX_RATE_BPS);
  const raw = priceNum * rate / 10000 * runwayWeeks;
  if (decimals <= 6) return BigInt(Math.floor(raw * 1e6));
  const [whole, frac = ''] = raw.toFixed(decimals).split('.');
  return BigInt(whole + frac.padEnd(decimals, '0').slice(0, decimals));
}

export function calcDepositStr(priceNum: number, runwayWeeks: number, taxRateBps?: bigint): string {
  if (priceNum <= 0) return '0.00';
  const rate = Number(taxRateBps ?? DEFAULT_TAX_RATE_BPS);
  const raw = priceNum * rate / 10000 * runwayWeeks;
  return raw < 0.01
    ? raw.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
    : raw.toFixed(2);
}

export interface TaxTokenConfig {
  isTakeover: boolean;
  symbol: string;
  decimals: number;
  tokenAddress: string;
  swapMode: SwapMode;
}

export function getTaxTokenConfig(taxToken?: string, usdcAddress?: string, indexerDecimals?: number): TaxTokenConfig {
  const addrMatch = !!taxToken && !!TAKEOVER_TOKEN_ADDRESS && taxToken.toLowerCase() === TAKEOVER_TOKEN_ADDRESS.toLowerCase();
  const isTakeover = addrMatch || (indexerDecimals != null && indexerDecimals > 6);
  const decimals = indexerDecimals ?? (isTakeover ? 18 : 6);
  return {
    isTakeover,
    symbol: isTakeover ? 'TAKEOVER' : 'USDC',
    decimals,
    tokenAddress: isTakeover ? (taxToken || TAKEOVER_TOKEN_ADDRESS) : (usdcAddress ?? ''),
    swapMode: isTakeover ? 'eth-takeover' : 'eth-usdc',
  };
}

export function formatTokenAmount(amount: bigint, config: TaxTokenConfig): string {
  if (config.isTakeover) {
    const num = parseFloat(formatUnits(amount, 18));
    return num < 0.01 ? num.toFixed(6) : num.toFixed(2);
  }
  const num = Number(amount) / 1e6;
  return num < 0.01 ? num.toFixed(6) : num.toFixed(2);
}

function compactNum(num: number): string {
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(2)}K`;
  if (abs > 0 && abs < 0.01) return `${sign}${abs.toFixed(6)}`;
  return `${sign}${abs.toFixed(2)}`;
}

export function formatTaxAmount(amount: bigint, config: TaxTokenConfig, options?: { showSign?: boolean }): string {
  if (config.isTakeover) {
    const num = parseFloat(formatUnits(amount, 18));
    const sign = options?.showSign && num > 0 ? '+' : '';
    return `${sign}${compactNum(num)} TAKEOVER`;
  }
  return formatUSD(amount, options);
}

export function formatTakeoverCompact(amount: bigint): string {
  const num = Number(amount) / 1e18;
  return compactNum(num);
}

export function takeoverToUsd(amount: bigint, priceUsd: number): string {
  const num = Number(amount) / 1e18;
  const usd = num * priceUsd;
  if (usd === 0) return '$0';
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(2)}B`;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(2)}M`;
  if (usd >= 1_000) return `$${(usd / 1_000).toFixed(2)}K`;
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(4)}`;
}

export { compactNum };

export function usdFromTakeover(amount: bigint, priceUsd: number): number {
  return (Number(amount) / 1e18) * priceUsd;
}

const MAX_SANE_USD_PER_SEAT = 10_000_000;

export function takeoverToUsdSafe(amount: bigint, priceUsd: number): string | null {
  if (priceUsd <= 0) return null;
  const usd = (Number(amount) / 1e18) * priceUsd;
  if (usd > MAX_SANE_USD_PER_SEAT) return null;
  return takeoverToUsd(amount, priceUsd);
}

export function usdFromTakeoverSafe(amount: bigint, priceUsd: number): number | null {
  if (priceUsd <= 0) return null;
  const usd = (Number(amount) / 1e18) * priceUsd;
  if (usd > MAX_SANE_USD_PER_SEAT) return null;
  return usd;
}

export function formatTaxAmountWithUsd(
  amount: bigint,
  config: TaxTokenConfig,
  priceUsd: number,
  options?: { showSign?: boolean },
): { primary: string; tokenHint: string | null } {
  if (!config.isTakeover || priceUsd <= 0) {
    return { primary: formatTaxAmount(amount, config, options), tokenHint: null };
  }
  const sign = options?.showSign && amount > 0n ? '+' : '';
  const primary = `${sign}${takeoverToUsd(amount, priceUsd)}`;
  const tokenHint = `~${formatTaxAmount(amount, config)}`;
  return { primary, tokenHint };
}

export function parseTokenPrice(price: string, decimals: number): bigint {
  if (decimals <= 6) return BigInt(Math.floor(parseFloat(price || '0') * 1e6));
  const num = parseFloat(price || '0');
  const [whole, frac = ''] = num.toFixed(decimals).split('.');
  return BigInt(whole + frac.padEnd(decimals, '0').slice(0, decimals));
}

/**
 * Format `newPrice` as the percentage gap below `reference`, used by the
 * shared "{N}% below the grid floor" warning across single + bulk
 * claim/buyout/manage drawers.
 *
 * Returns:
 *   - `''` when `reference` is non-positive or `newPrice >= reference`
 *     (callers gate on the same condition before rendering, so the
 *     empty-string return is just a defensive belt).
 *   - `'<0.1%'` when the gap is positive but rounds to zero tenths —
 *     honest about the threshold being crossed without claiming a `0%`
 *     gap that would contradict the warning.
 *   - One-decimal precision otherwise (`'2.5%'`, `'17%'`), trimming the
 *     trailing `.0` so whole-number gaps read as integers.
 *
 * Computes via bigint arithmetic (`(diff * 1000n) / reference`) so 18-
 * decimal TAKEOVER prices don't lose precision through a `Number()`
 * round-trip — `Number(diff)` on a 1e21-scale value would truncate the
 * relevant low bits before the division ran.
 */
export function formatPercentBelow(newPrice: bigint, reference: bigint): string {
  if (reference <= 0n || newPrice >= reference) return '';
  const tenths = Number(((reference - newPrice) * 1000n) / reference);
  if (tenths <= 0) return '<0.1%';
  if (tenths % 10 === 0) return `${tenths / 10}%`;
  return `${(tenths / 10).toFixed(1)}%`;
}

export interface SeatRPCResult {
  seatIndex: number;
  holder: string;
  selfAssessedPrice: bigint;
  balance: bigint;
  lastUpdateTime: bigint;
  lastPriceChangeTime: bigint;
  isActive: boolean;
  /**
   * V22 `seats()` tuple slot — unix seconds when the seat was forfeited, else 0.
   * Undefined when read via V1 manager ABI (no such field).
   */
  forfeitedAt?: number;
}

async function getPublicClient() {
  const { createPublicClient, http } = await import('viem');
  const { base, baseSepolia } = await import('viem/chains');
  const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
  const chain = chainId === '8453' ? base : baseSepolia;
  const rpcUrl = process.env.NEXT_PUBLIC_RPC_URL || chain.rpcUrls.default.http[0];
  // batch.multicall lets viem auto-aggregate concurrent readContract calls
  // (e.g. the 100-seat refresh after a full takeover) into a single multicall3
  // request, avoiding 429s on the proxied upstream RPC.
  return createPublicClient({
    chain,
    transport: http(rpcUrl),
    batch: { multicall: true },
  });
}

/** Resolve the contract argument for a given pool/grid id.
 *
 * V2 managers (UGM) take a `uint256 gridId`; V1 managers take a
 * `bytes32 poolId`. The drawer code threads the indexer-shaped
 * composite id (`{managerAddress}-{numericId}`) through every refresh
 * call site, so a raw `BigInt(poolId)` here threw
 * "Cannot convert 0x…-3 to a BigInt" — the symptom users hit when a
 * `setPrice` / `addDeposit` `onSuccess` ran. `parseGridId` falls back
 * to the bare id for legacy numeric ids, so this is safe for both
 * shapes.
 */
function seatsContractArg(managerAddress: Hex, poolId: string): bigint | Hex {
  if (isV2Manager(managerAddress)) {
    return BigInt(parseGridId(poolId).numericId);
  }
  return poolId as Hex;
}

export async function readSeatFromRPC(
  managerAddress: Hex,
  poolId: string,
  seatIndex: number
): Promise<SeatRPCResult> {
  const publicClient = await getPublicClient();
  const v2 = isV2Manager(managerAddress);
  const poolArg = seatsContractArg(managerAddress, poolId);

  const result = await publicClient.readContract({
    address: managerAddress,
    abi: v2 ? UGM_ABI : TAKEOVER_MANAGER_ABI,
    functionName: 'seats',
    args: [poolArg, BigInt(seatIndex)],
  }) as
    | readonly [string, bigint, bigint, bigint, bigint]
    | readonly [string, bigint, bigint, bigint, bigint, bigint, bigint, boolean];

  if (v2 && result.length >= 8) {
    const [
      holder,
      selfAssessedPrice,
      balance,
      lastUpdateTime,
      lastPriceChangeTime,
      ,
      forfeitedRaw,
    ] = result;
    const isActive = holder !== '0x0000000000000000000000000000000000000000';
    const fa = Number(forfeitedRaw);
    return {
      seatIndex,
      holder,
      selfAssessedPrice,
      balance,
      lastUpdateTime,
      lastPriceChangeTime,
      isActive,
      forfeitedAt: Number.isFinite(fa) ? fa : undefined,
    };
  }

  const [holder, selfAssessedPrice, balance, lastUpdateTime, lastPriceChangeTime] = result as readonly [
    string,
    bigint,
    bigint,
    bigint,
    bigint,
  ];
  const isActive = holder !== '0x0000000000000000000000000000000000000000';

  return { seatIndex, holder, selfAssessedPrice, balance, lastUpdateTime, lastPriceChangeTime, isActive };
}

export async function readSeatsFromRPC(
  managerAddress: Hex,
  poolId: string,
  seatIndices: number[]
): Promise<SeatRPCResult[]> {
  const publicClient = await getPublicClient();
  const v2 = isV2Manager(managerAddress);
  const poolArg = seatsContractArg(managerAddress, poolId);

  const results = await Promise.all(
    seatIndices.map(async (seatIndex) => {
      const result = await publicClient.readContract({
        address: managerAddress,
        abi: v2 ? UGM_ABI : TAKEOVER_MANAGER_ABI,
        functionName: 'seats',
        args: [poolArg, BigInt(seatIndex)],
      }) as
        | readonly [string, bigint, bigint, bigint, bigint]
        | readonly [string, bigint, bigint, bigint, bigint, bigint, bigint, boolean];

      if (v2 && result.length >= 8) {
        const [
          holder,
          selfAssessedPrice,
          balance,
          lastUpdateTime,
          lastPriceChangeTime,
          ,
          forfeitedRaw,
        ] = result;
        const isActive = holder !== '0x0000000000000000000000000000000000000000';
        const fa = Number(forfeitedRaw);
        return {
          seatIndex,
          holder,
          selfAssessedPrice,
          balance,
          lastUpdateTime,
          lastPriceChangeTime,
          isActive,
          forfeitedAt: Number.isFinite(fa) ? fa : undefined,
        };
      }

      const [holder, selfAssessedPrice, balance, lastUpdateTime, lastPriceChangeTime] =
        result as readonly [string, bigint, bigint, bigint, bigint];
      const isActive = holder !== '0x0000000000000000000000000000000000000000';

      return { seatIndex, holder, selfAssessedPrice, balance, lastUpdateTime, lastPriceChangeTime, isActive };
    })
  );

  return results;
}

export function updateSeatsInCache(oldData: any, seatResults: SeatRPCResult[]): any {
  if (!oldData?.data?.seats) return oldData;

  const resultMap = new Map(seatResults.map(r => [r.seatIndex, r]));
  // Stamp `rpcCurrentBalanceAt = now` because `setPrice`/`addDeposit`/
  // `withdrawDeposit`/`buyOut`/etc. all `_applyTax` first, which sets
  // `seat.lastTaxTime = block.timestamp`. After the tx, the stored
  // `deposit` IS the live `currentBalance`, so the post-tx anchor is
  // exactly "now". Without this stamp, the client-side decay in
  // `buildSeatData` would treat the post-tx deposit as if it had been
  // sampled at unix-0 and immediately project the seat into forfeit.
  const sampledAt = Math.floor(Date.now() / 1000);

  const newSeats = oldData.data.seats.map((s: any) => {
    const result = resultMap.get(s.seatIndex);
    if (!result) return s;
    const holder = result.isActive ? result.holder : null;
    const holderChanged = holder?.toLowerCase() !== s.holder_id?.toLowerCase();
    return {
      ...s,
      holder,
      holder_id: holder,
      ...(holderChanged && { holderIdentity: undefined }),
      selfAssessedPrice: result.selfAssessedPrice.toString(),
      balance: result.balance.toString(),
      deposit: result.balance.toString(),
      // `buildSeatData` prefers `rpcCurrentBalance` over the cached
      // `balance`/`deposit` pair when present (see
      // `use-grid-page.helpers.ts`), so leaving this stale meant the
      // post-takeover UI kept reading the previous holder's near-zero
      // balance and showed e.g. "30 minutes left" on a tile that
      // actually has an hour of runway, plus surfaced the
      // "Pending Forfeit" tooltip immediately after a successful
      // buyout. Mirror the freshly-RPC-read balance into the same slot
      // so every consumer reads the same authoritative value until the
      // indexer catches up.
      rpcCurrentBalance: result.balance.toString(),
      rpcCurrentBalanceAt: sampledAt,
      ...(typeof result.forfeitedAt === 'number'
        ? {
            rpcForfeitedAt: result.forfeitedAt > 0 ? result.forfeitedAt : undefined,
          }
        : {}),
      lastUpdateTime: Number(result.lastUpdateTime),
      lastTaxTime: Number(result.lastUpdateTime),
      lastPriceChangeTime: Number(result.lastPriceChangeTime),
      isActive: result.isActive,
    };
  });

  return { ...oldData, data: { ...oldData.data, seats: newSeats } };
}

const SECONDS_PER_WEEK = 7n * 24n * 3600n;

/**
 * Patch every cached portfolio JSON snapshot so any seat matching the
 * given `poolId + seatIndex` reflects the freshly-RPC-read state.
 *
 * Why this exists:
 *   The lobby manage modal (`TileManagementModal`) reads from the
 *   `['wallet', 'portfolio', addr]` cache via `useLobbySeatHoldings`,
 *   which goes through the indexer-backed `/api/portfolio/[addr]`
 *   endpoint. After a `setPrice` / `addDeposit` / `withdrawDeposit`
 *   transaction the indexer typically lags the chain by 1–3 seconds —
 *   long enough that the modal would still show the stale value when a
 *   user finished the txn and went back to manage the next tile.
 *
 *   `useGridPage` already gets the same RPC values written into its
 *   `['v2-grid', poolId]` cache by `refreshAfterTransaction`, which is
 *   why the floating "My Tiles" panel updates instantly. Mirroring that
 *   write into the portfolio cache gives the modal the same
 *   instantly-fresh experience, removing the user-visible "I have to
 *   refresh after every change" footgun.
 *
 *   The lookup uses `getQueriesData({ queryKey: ['wallet', 'portfolio'] })`
 *   so we don't need the wallet address as a parameter — every observed
 *   portfolio cache (including any prefetched ones) gets patched.
 */
export function updatePortfolioSeatsInCache(
  queryClient: QueryClient,
  poolId: string,
  seatResults: SeatRPCResult[],
): void {
  if (seatResults.length === 0) return;
  const all = queryClient.getQueriesData<any>({ queryKey: ['wallet', 'portfolio'] });
  if (all.length === 0) return;

  const resultMap = new Map(seatResults.map((r) => [r.seatIndex, r]));

  for (const [key, oldData] of all) {
    if (!oldData?.data?.seats) continue;

    let mutated = false;
    const next: any[] = [];
    for (const s of oldData.data.seats as any[]) {
      const seatPool = s.pool?.id ?? s.poolId;
      if (seatPool !== poolId) {
        next.push(s);
        continue;
      }
      const result = resultMap.get(s.seatIndex);
      if (!result) {
        next.push(s);
        continue;
      }
      mutated = true;

      // Abandoned seats vanish from the portfolio fetcher (which only
      // queries `isActive: true` rows). Mirror that here so the modal's
      // row count converges with the chip count immediately on abandon.
      if (!result.isActive) continue;

      const taxBps = BigInt(s.pool?.taxRateBps ?? 500);
      const newPrice = result.selfAssessedPrice;
      const newBalance = result.balance;
      const weeklyTax = taxBps > 0n ? (newPrice * taxBps) / 10000n : 0n;
      const timeUntilForfeiture =
        weeklyTax > 0n ? Number((newBalance * SECONDS_PER_WEEK) / weeklyTax) : 0;

      next.push({
        ...s,
        holder: result.holder,
        selfAssessedPrice: newPrice.toString(),
        balance: newBalance.toString(),
        // Mirror the freshly-RPC-read currentBalance into both the
        // legacy root field (`portfolio.ts` stamps it for parity with
        // the grid-seat shape) and the structured `metrics.*` block
        // the lobby holdings hook actually reads. Without the root
        // mirror, the next portfolio refetch's `lastPriceChangeTime`
        // tie-break in `mergePortfolioPreservingCachedSeats` could
        // surface a row whose root `rpcCurrentBalance` lags the
        // structured one — and any future reader switching to the
        // root field would then see the stale value.
        rpcCurrentBalance: newBalance.toString(),
        // Stamp the sample time alongside the balance so any consumer
        // applying continuous client-side tax decay (the lobby My
        // Tiles / tooltip path, eventually the manage modal) decays
        // from the correct anchor instead of treating the post-tx
        // value as if it had been sampled at unix-0.
        rpcCurrentBalanceAt: Math.floor(Date.now() / 1000),
        lastUpdateTime: Number(result.lastUpdateTime),
        lastPriceChangeTime: Number(result.lastPriceChangeTime),
        isActive: true,
        metrics: {
          ...(s.metrics ?? {}),
          currentBalance: newBalance.toString(),
          weeklyTax: weeklyTax.toString(),
          timeUntilForfeiture,
        },
      });
    }
    if (!mutated) continue;

    queryClient.setQueryData(key, {
      ...oldData,
      data: { ...oldData.data, seats: next },
    });
  }
}

/**
 * Merge a freshly-fetched portfolio JSON with whatever's already in the
 * cache so RPC-patched seats survive a stale indexer refetch.
 *
 * `useLobbySeatHoldings` and `useMeSummary` both go through this on the
 * read side: whenever the underlying fetch lands a payload, we keep any
 * cached seat whose `lastPriceChangeTime` (or `lastUpdateTime` for pure
 * deposit/withdraw flows that don't bump price) is newer than the
 * indexer's. Mirrors the same merge `useGridPage` does for `['v2-grid',
 * poolId]` (see `use-grid-page.ts` ~line 145) — without it the
 * post-transaction RPC patch from `updatePortfolioSeatsInCache` would
 * be stomped by the next portfolio refetch and the user would see the
 * old value flicker back until the indexer caught up.
 */
export function mergePortfolioPreservingCachedSeats(
  cached: any,
  fresh: any,
): any {
  if (!fresh) return fresh;
  const cachedSeats = cached?.data?.seats;
  const freshSeats = fresh?.data?.seats;
  if (!Array.isArray(cachedSeats) || !Array.isArray(freshSeats)) return fresh;

  const cachedMap = new Map<string, any>();
  for (const cs of cachedSeats) {
    const seatPool = cs.pool?.id ?? cs.poolId ?? '';
    cachedMap.set(`${seatPool}:${cs.seatIndex}`, cs);
  }

  let mutated = false;
  const merged = freshSeats.map((s: any) => {
    const seatPool = s.pool?.id ?? s.poolId ?? '';
    const prev = cachedMap.get(`${seatPool}:${s.seatIndex}`);
    if (!prev) return s;

    const cachedPriceTs = Number(prev.lastPriceChangeTime ?? 0);
    const freshPriceTs = Number(s.lastPriceChangeTime ?? 0);
    const cachedUpdateTs = Number(prev.lastUpdateTime ?? 0);
    const freshUpdateTs = Number(s.lastUpdateTime ?? 0);
    if (cachedPriceTs > freshPriceTs || cachedUpdateTs > freshUpdateTs) {
      mutated = true;
      return prev;
    }
    return s;
  });

  if (!mutated) return fresh;
  return { ...fresh, data: { ...fresh.data, seats: merged } };
}

export async function refreshSeatFromRPC(
  queryClient: QueryClient,
  managerAddress: Hex,
  poolId: string,
  coinAddress: string | undefined,
  seatIndex: number,
  onSeatUpdate?: (seatIndex: number, holder: string | null, price: string) => void,
) {
  try {
    const result = await readSeatFromRPC(managerAddress, poolId, seatIndex);

    const updater = (oldData: any) => updateSeatsInCache(oldData, [result]);

    queryClient.setQueryData(['pool', poolId], updater);
    queryClient.setQueryData(['v2-grid', poolId], updater);
    // Mirror the same write into the wallet portfolio cache so the
    // lobby manage modal (which reads `useLobbySeatHoldings`) reflects
    // the txn instantly instead of waiting for the indexer to catch up.
    updatePortfolioSeatsInCache(queryClient, poolId, [result]);

    onSeatUpdate?.(seatIndex, result.isActive ? result.holder : null, result.selfAssessedPrice.toString());
  } catch (error) {
    console.error('[drawer-utils] Failed to refresh seat from RPC:', error);
  }
}

export async function refreshSeatsFromRPC(
  queryClient: QueryClient,
  managerAddress: Hex,
  poolId: string,
  coinAddress: string | undefined,
  seatIndices: number[],
  onSeatUpdate?: (seatIndex: number, holder: string | null, price: string) => void,
) {
  try {
    const results = await readSeatsFromRPC(managerAddress, poolId, seatIndices);

    const updater = (oldData: any) => updateSeatsInCache(oldData, results);

    queryClient.setQueryData(['pool', poolId], updater);
    queryClient.setQueryData(['v2-grid', poolId], updater);
    updatePortfolioSeatsInCache(queryClient, poolId, results);

    for (const result of results) {
      onSeatUpdate?.(result.seatIndex, result.isActive ? result.holder : null, result.selfAssessedPrice.toString());
    }
  } catch (error) {
    console.error('[drawer-utils] Failed to refresh seats from RPC:', error);
  }
}

export async function refreshAfterTransaction(
  queryClient: QueryClient,
  managerAddress: Hex,
  poolId: string,
  coinAddress: string | undefined,
  seatIndices: number[],
  onSeatUpdate?: (seatIndex: number, holder: string | null, price: string) => void,
) {
  if (seatIndices.length === 1) {
    await refreshSeatFromRPC(queryClient, managerAddress, poolId, coinAddress, seatIndices[0], onSeatUpdate);
  } else {
    await refreshSeatsFromRPC(queryClient, managerAddress, poolId, coinAddress, seatIndices, onSeatUpdate);
  }

  await queryClient.invalidateQueries({ queryKey: ['wallet'], refetchType: 'all' });
  // Force the manage / bulk drawers' `useLiveBalances` poll to refire
  // immediately. Without this the React Query interval (30s default)
  // continues serving the pre-tx snapshot for up to ~30s after a
  // withdraw/deposit settles, so the drawer's "minutes left" reads
  // the OLD (larger) balance + the new price → a falsely-long runway
  // until the next poll. The same affects bulk mode and the profile
  // page. `refetchType: 'active'` keeps the invalidation cheap —
  // only views currently mounted hit the network.
  await queryClient.invalidateQueries({ queryKey: ['live-balances'], refetchType: 'active' });
  setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: ['pool-events', poolId], refetchType: 'all' });
  }, 3000);
}
